<template>

    <div class="empty">

        <div class="bloc">

            <h1 class="title">{{ title }}</h1>

            <p class="text">{{ text }}</p>

            <div class="belt">

                <button v-show="fetchDoneWithoutNewsletters"
                        class="cta-add"
                        @click="goAddInbox">
                    {{ $t('cleaning_done_add_mailbox_cta') }}
                </button>

                <button v-show="fetchDoneWithoutNewsletters"
                        class="cta-stats"
                        @click="goStats">
                    {{ $t('cleaning_list_cta_stat') }}
                </button>

                <button v-show="!fetchDoneWithoutNewsletters"
                        class="cta-refresh"
                        :class="{'cta-disable': !newslettersWaiting}"
                        @click="ctaRefresh">
                    {{ $t('cleaning_scan_done_refresh') }}
                </button>

            </div>

            <lottie-clean-up ref="cleanUp" :loop="true" :autoplay="true"></lottie-clean-up>

        </div>

        <div class="notifications" v-if="!isFetchDone">
            <notifications-ask-bloc></notifications-ask-bloc>
        </div>

    </div>

</template>

<script>
import Vuex from 'vuex'

import ENUM from '../../assets/ENUM'
import Tag from '@/modules/tag'

import LottieCleanUp from '../lottie/LottieCleanUp'
import NotificationsAskBloc from '../../components/NotificationsAskBloc'

export default {

    name: 'empty',
    components: {
        LottieCleanUp,
        NotificationsAskBloc
    },

    props: {
        category: {
            type: String,
            default: ''
        }
    },

    computed: {

        ...Vuex.mapGetters(['fetchStatus', 'newslettersWaiting', 'newsletters', 'newslettersTemp']),

        title() {
            if (this.fetchSortableWithoutNewsletters) {
                return this.$tc('partial_fetch_end_preview_title', this.newslettersTemp.length, {'nb_partial': this.newslettersTemp.length})
            } else if (this.fetchSortableNewslettersWaiting) {
                return this.$t('partial_fetch_new_senders_detected')
            } else if (this.fetchDoneNewslettersWaiting) {
                return this.$t('partial_fetch_scan_done_title')
            } else {
                // fetchDoneWithoutNewsletters:
                return this.$t('cleaning_clean_title')
            }
        },

        text() {
            if (this.fetchSortableWithoutNewsletters) {
                return this.$t('partial_fetch_end_preview_subtitle')
            } else if (this.fetchSortableNewslettersWaiting) {
                return this.$t('partial_fetch_start_sorting')
            } else if (this.fetchDoneNewslettersWaiting) {
                return this.$t('partial_fetch_scan_done_desc')
            } else {
                // fetchDoneWithoutNewsletters:
                return this.$t('cleaning_list_end')
            }
        },

        isFetchDone() {
            return this.fetchStatus === ENUM.FETCH_STATUS.DONE
        },

        fetchSortableWithoutNewsletters() {
            return !this.isFetchDone && !this.newslettersWaiting
        },

        fetchSortableNewslettersWaiting() {
            return !this.isFetchDone && this.newslettersWaiting
        },

        fetchDoneNewslettersWaiting() {
            return this.isFetchDone && this.newslettersWaiting
        },

        fetchDoneWithoutNewsletters() {
            return this.isFetchDone && !this.newslettersWaiting
        }
    },

    mounted() {
        Tag.pageViewGA(this, 'cleaning_done')
        this.startAnimation()
    },

    methods: {
        ...Vuex.mapActions([
            'updateNewsletters'
        ]),

        goAddInbox() {
            Tag.clickGA(this, 'add_inbox')
            this.$router.push({path: '/add-inbox'})
        },

        goStats() {
            Tag.clickGA(this, 'see_stats')
            this.$router.push({path: '/stats'})
        },

        ctaRefresh() {
            if (this.newslettersWaiting) {
                this.updateNewsletters()
            }
        },

        startAnimation() {
            setTimeout(() => {
                if (this.$refs.cleanUp) {
                    this.$refs.cleanUp.pause()
                }
            }, 6000)
        }
    }
}
</script>

<style lang="scss" scoped>

.empty {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 90px 16px 24px;
    background-color: $cultured;
    text-align: center;
    @media screen and (min-width: $above-tablet-length) {
        padding: 16px 32px 32px;
        justify-content: center;
    }

    .bloc {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 463px;

        > img {
            height: 80px;
            object-fit: contain;
            @media screen and (min-width: $above-tablet-length) {
                height: 96px;
            }
        }

        .circle {
            width: 40px;
            height: 40px;
            background-color: $white;
            border-radius: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            > img {
                width: 16px;
                object-fit: contain;
            }
        }

        .title {
            margin-top: 16px;
            font-size: 17px;
            font-weight: 600;
            line-height: 1.41;
            color: $midnight;
        }

        .text {
            margin-top: 8px;
            font-size: 15px;
            font-weight: 500;
            line-height: 1.33;
            color: $neutralsMoon;
        }

        button {
            padding: 16px 40px;
            width: 100%;
            border-radius: 32px;
            text-align: center;
            font-size: 17px;
            font-weight: 600;
            line-height: 1.41;
            @media screen and (min-width: $above-tablet-length) {
                max-width: 308px;
            }
        }

        .cta-add {
            width: 100%;
            margin-top: 32px;
            background-color: $aquamarine;
            color: $white;
        }

        .cta-stats {
            width: 100%;
            margin-top: 8px;
            background-color: $white;
            color: $charcoal;
            @media screen and (min-width: $above-tablet-length) {
                border: 4px solid $neutralsPlatinum;
                padding: 12px;
            }
        }

        .cta-refresh {
            width: 100%;
            margin-top: 24px;
            background-color: $white;
            color: $neutralsMoon;
            border: 4px solid $silver;
            font-size: 17px;
            font-weight: 600;
            line-height: 24px;
        }

        .cta-disable {
            cursor: not-allowed;
            background-color: $timberwolf;
            color: $silver;
            border: 4px solid $silver;
        }

        .belt {
            display: flex;
            flex-direction: column;
        }
    }

    .notifications {
        margin: 16px 16px 0;

        @media screen and (min-width: $above-tablet-length) {
            margin: 0;
            position: absolute;
            bottom: 40px;
        }
    }
}
</style>
